import camelcaseKeys from 'camelcase-keys';
import { defineStore } from 'pinia';
import FormsAPI from 'dashboard/api/forms';

const camelize = data => camelcaseKeys(data || {}, { deep: true });

// A chave de cada campo do formulário ("contact|standard|name") não pode ser camelizada,
// então o `config` volta do servidor como veio e só o resto do registro é camelizado.
const toRecord = ({ config, ...rest }) => ({ ...camelize(rest), config });

export const useFormsStore = defineStore('forms', {
  state: () => ({
    records: [],
    uiFlags: { fetchingList: false, saving: false },
  }),

  getters: {
    getFormById: state => id => state.records.find(form => form.id === id),
  },

  actions: {
    upsert(record) {
      const index = this.records.findIndex(form => form.id === record.id);
      if (index === -1) this.records.unshift(record);
      else this.records[index] = record;
    },

    async get() {
      this.uiFlags.fetchingList = true;
      try {
        const { data } = await FormsAPI.get();
        this.records = data.payload.map(toRecord);
        return this.records;
      } finally {
        this.uiFlags.fetchingList = false;
      }
    },

    async show(id) {
      const { data } = await FormsAPI.show(id);
      const record = toRecord(data.payload);
      this.upsert(record);
      return record;
    },

    async save({ id, ...attrs }) {
      this.uiFlags.saving = true;
      try {
        const { data } = await (id
          ? FormsAPI.update(id, attrs)
          : FormsAPI.create(attrs));
        const record = toRecord(data.payload);
        this.upsert(record);
        return record;
      } finally {
        this.uiFlags.saving = false;
      }
    },

    async delete(id) {
      await FormsAPI.destroy(id);
      this.records = this.records.filter(form => form.id !== id);
    },

    async getSubmissions(id, params) {
      const { data } = await FormsAPI.getSubmissions(id, params);
      return {
        submissions: data.payload.map(({ payload, attribution, ...rest }) => ({
          ...camelize(rest),
          payload,
          attribution,
        })),
        meta: camelize(data.meta),
      };
    },

    async getAnalytics(id) {
      const { data } = await FormsAPI.getAnalytics(id);
      return data.payload.map(camelize);
    },
  },
});
